import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const serviceCategories = [
  {
    id: "creators",
    tag: "For Creators & Hosts",
    title: "Creator & Host Services",
    subtitle: "Full-spectrum support from day one, across every platform",
    icon: "ri-user-star-line",
    services: [
      { icon: "ri-broadcast-line", name: "Live platform onboarding and training" },
      { icon: "ri-shield-check-line", name: "Content, compliance, and engagement coaching" },
      { icon: "ri-calendar-event-line", name: "Weekly show concepts and content planning" },
      { icon: "ri-money-dollar-circle-line", name: "Monetization coaching" },
      { icon: "ri-bar-chart-2-line", name: "Performance check-ins" },
    ],
    bg: "bg-brand-black",
    imageUrl:
      "https://public.readdy.ai/ai/img_res/8b40bfc03808d88a9a0a6bc0a9c779cc.jpg",
  },
  {
    id: "branding",
    tag: "Branding & Content",
    title: "Branding & Content Development",
    subtitle: "Building your identity across every channel",
    icon: "ri-palette-line",
    services: [
      { icon: "ri-user-settings-line", name: "Profile optimization" },
      { icon: "ri-star-line", name: "Personal brand identity" },
      { icon: "ri-links-line", name: "Social media alignment" },
      { icon: "ri-camera-lens-line", name: "Visual direction" },
    ],
    bg: "bg-brand-dark",
    imageUrl:
      "https://readdy.ai/api/search-image?query=luxury%20brand%20identity%20design%20concept%20dark%20background%20gold%20accent%20creative%20branding%20elements%20typography%20visual%20identity%20premium%20agency%20aesthetic&width=700&height=500&seq=srv002&orientation=landscape",
  },
  {
    id: "growth",
    tag: "Creator Growth",
    title: "Creator Growth Strategy",
    subtitle: "Sustainable growth built on real data and community",
    icon: "ri-line-chart-line",
    services: [
      { icon: "ri-group-line", name: "Audience retention" },
      { icon: "ri-heart-pulse-line", name: "Engagement systems" },
      { icon: "ri-target-line", name: "Goal setting and performance tracking" },
    ],
    bg: "bg-brand-black",
    imageUrl:
      "https://readdy.ai/api/search-image?query=digital%20analytics%20dashboard%20dark%20theme%20gold%20glowing%20data%20charts%20metrics%20performance%20tracking%20professional%20dark%20background%20minimal%20clean&width=700&height=500&seq=srv003&orientation=landscape",
  },
  {
    id: "brands",
    tag: "For Brands",
    title: "For Brands & Businesses",
    subtitle: "Connecting your brand with engaged audiences everywhere",
    icon: "ri-building-2-line",
    services: [
      { icon: "ri-live-line", name: "Live campaign production" },
      { icon: "ri-user-search-line", name: "Host casting" },
      { icon: "ri-calendar-check-line", name: "Live event coordination" },
      { icon: "ri-handshake-line", name: "Influencer partnerships" },
      { icon: "ri-settings-4-line", name: "Campaign management" },
      { icon: "ri-pie-chart-2-line", name: "Reporting and analytics" },
      { icon: "ri-lightbulb-line", name: "Creative consulting" },
    ],
    bg: "bg-brand-dark",
    imageUrl:
      "https://readdy.ai/api/search-image?query=luxury%20brand%20campaign%20live%20streaming%20production%20dark%20studio%20gold%20neon%20accents%20professional%20marketing%20team%20broadcast%20business%20dark%20theme&width=700&height=500&seq=srv004&orientation=landscape",
  },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Creator & Host Management Services",
  provider: {
    "@type": "Organization",
    name: "The LittyCity Agency LLC",
    url: import.meta.env.VITE_SITE_URL || "https://example.com",
  },
  url: `${import.meta.env.VITE_SITE_URL || "https://example.com"}/services`,
  description:
    "The LittyCity Agency LLC offers full-spectrum creator and host management services including live platform onboarding, branding, content strategy, growth coaching, and brand campaign management across all major social media platforms.",
  serviceType: [
    "Creator Management",
    "Host Management",
    "Brand Campaign Management",
    "Content Strategy",
    "Social Media Growth",
  ],
  areaServed: "US",
};

function ServiceCard({
  category,
  index,
}: {
  category: (typeof serviceCategories)[0];
  index: number;
}) {
  const isReversed = index % 2 !== 0;
  return (
    <section className={`${category.bg} py-24 md:py-32 section-divider`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
            isReversed ? "lg:flex-row-reverse" : ""
          }`}
        >
          {/* Content */}
          <div className={`reveal ${isReversed ? "lg:order-2" : ""}`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 mb-6">
              <i className={`${category.icon} text-gold text-base`} />
              <span className="text-gold text-xs font-sans font-semibold tracking-[0.2em] uppercase">
                {category.tag}
              </span>
            </div>
            <h2
              className="font-display font-black text-white leading-tight mb-3"
              style={{ fontSize: "clamp(1.8rem, 3.5vw, 3rem)" }}
            >
              {category.title}
            </h2>
            <p className="text-white/50 font-sans text-base mb-8">{category.subtitle}</p>
            <div className="flex flex-col gap-3">
              {category.services.map((svc) => (
                <div
                  key={svc.name}
                  className="flex items-center gap-4 py-3 px-4 rounded-xl bg-brand-card/50 border border-gold/10 group hover:border-gold/30 transition-all duration-300"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gold/10 flex-shrink-0">
                    <i className={`${svc.icon} text-gold text-base`} />
                  </div>
                  <span className="text-white/80 font-sans text-sm">{svc.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div
            className={`reveal reveal-delay-1 ${isReversed ? "lg:order-1" : ""}`}
          >
            <div className="relative rounded-2xl overflow-hidden gold-glow">
              <div className="w-full h-72 md:h-80">
                <img
                  src={category.imageUrl}
                  alt={category.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    const id = "schema-services";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(SCHEMA);
    document.title = "Our Services | The LittyCity Agency LLC";

    return () => {
      observer.disconnect();
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-brand-black">
      <Navbar />
      {/* Header */}
      <div className="pt-36 pb-20 bg-brand-black text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-gold/4 blur-3xl" />
        </div>
        <div className="relative z-10">
          <span className="text-gold text-xs font-sans font-semibold tracking-[0.3em] uppercase block mb-5">
            What We Offer
          </span>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl leading-none mb-6">
            Our <span className="text-gradient-gold">Services</span>
          </h1>
          <p className="text-white/50 font-sans text-lg max-w-xl mx-auto">
            From creator onboarding to brand campaigns — we handle it all.
          </p>
          <div className="w-24 h-px bg-gold mx-auto mt-8" />
        </div>
      </div>

      <main>
        {serviceCategories.map((cat, i) => (
          <ServiceCard key={cat.id} category={cat} index={i} />
        ))}

        {/* CTA */}
        <section className="bg-brand-black py-28 text-center">
          <div className="max-w-2xl mx-auto px-6">
            <div className="reveal">
              <h2 className="font-display font-black text-white text-4xl md:text-5xl mb-6">
                Ready to <span className="text-gradient-gold">Work Together?</span>
              </h2>
              <p className="text-white/50 font-sans text-lg mb-10">
                Whether you&apos;re a creator looking to grow or a brand ready to
                launch, we&apos;re here for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/join-bigo"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-sans font-semibold text-sm tracking-widest cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line" />
                  Join Our Creator Network
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-gold text-gold font-sans font-medium text-sm tracking-widest hover:bg-gold hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-line" />
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
